<template>
  <modal 
    name="agreements-warnings" 
    class="modal-inner modal-agreements-warnings" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    height="auto" 
    :scrollable="true"
  >
    <span class="close-button" @click="hide('agreements-warnings')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t('agreements.warnings_modal_title') }}
    </div>
    <div class="modal-body">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('agreements.warnings_filter') }}
          </label>
          <select 
            v-model="warningFilter"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">{{ $t('agreements.warnings_all') }}</option>
            <option value="invoice_without_pdf">{{ $t('agreements.warnings.invoice_without_pdf') }}</option>
            <option value="without_alarms">{{ $t('agreements.warnings.without_alarms') }}</option>
            <option value="extended">{{ $t('agreements.warnings.extended') }}</option>
            <option value="dismissed_past_cancellation_date">{{ $t('agreements.warnings.dismissed_past_cancellation_date') }}</option>
            <option value="dismissed_with_devices">{{ $t('agreements.warnings.dismissed_with_devices') }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('agreements.warnings_search') }}
          </label>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            :placeholder="$t('agreements.warnings_search_placeholder')"
          />
        </div>
      </div>

      <vue-good-table
        styleClass="vgt-table sd-table"
        :columns="columns"
        :rows="filteredWarnings"
        :search-options="{ enabled: false }"
        :pagination-options="{ enabled: true, perPage: 50, perPageDropdown: [50, 100, 200] }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field == 'customer_name'">
            <div class="flex flex-col">
              <span class="font-medium text-gray-800">{{ props.row.customer_name }}</span>
              <span class="text-xs text-gray-500">{{ props.row.agreement_number }}</span>
            </div>
          </span>
          <span v-else-if="props.column.field == 'status_label'">
            <span :class="getStatusBadgeClass(props.row._agreement?.status)">
              {{ props.row.status_label }}
            </span>
          </span>
          <span v-else-if="props.column.field == 'warning_types_text'">
            <div class="flex flex-wrap gap-1">
              <span 
                v-for="(label, idx) in props.row.warning_labels" 
                :key="idx"
                :class="getWarningBadgeClass(props.row.warning_keys[idx])"
              >
                {{ label }}
              </span>
            </div>
          </span>
          <span v-else-if="props.column.field == 'menu'">
            <button 
              v-if="canActOnRow(props.row._agreement)"
              class="btn-secondary-outline p-2"
              @click.prevent="$emit('open-menu', props.row._agreement, $event)"
            >
              <BaseIcon icon="bars" />
            </button>
          </span>
          <span v-else-if="props.column.field == 'edit'">
            <button 
              v-if="canActOnRow(props.row._agreement)"
              class="sd-btn sd-btn--icon sd-btn--secondary" 
              @click.prevent="handleEditAgreement(props.row._agreement)"
            >
              <BaseIcon icon="pencil-alt" />
            </button>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
        <div slot="emptystate" class="flex flex-wrap flex-col items-center my-5 text-sm text-gray-500">
          {{ $t('agreements.no_agreements_found') }}
        </div>
      </vue-good-table>
    </div>
  </modal>
</template>

<script>
export default {
  props: {
    warnings: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canActFn: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      warningFilter: 'all',
      searchQuery: ''
    };
  },
  computed: {
    modalWidth() {
      if (window.innerWidth < 768) return "100%";
      return "1200px";
    },
    columns() {
      return [
        { label: this.$t('my_agreement_table.name'), field: 'customer_name', sortable: false },
        { label: this.$t('agreements.status'), field: 'status_label', sortable: false },
        { label: this.$t('agreements.warnings_label'), field: 'warning_types_text', sortable: false },
        { label: this.$t('agreements.warnings_short'), field: 'warning_count', sortable: false, tdClass: 'whitespace-nowrap w-12 text-center', thClass: 'whitespace-nowrap w-12 text-center' },
        { label: this.$t('agreements.start_short'), field: 'contract_start', sortable: false },
        { label: this.$t('agreements.period_short'), field: 'contract_period', sortable: false, tdClass: 'whitespace-nowrap w-14 text-center', thClass: 'whitespace-nowrap w-14 text-center' },
        { label: this.$t('my_agreement_table.seller'), field: 'seller_name', sortable: false },
        { label: this.$t('agreements.rent_short'), field: 'rental_month_cost', sortable: false, tdClass: 'whitespace-nowrap w-16 text-right', thClass: 'whitespace-nowrap w-16 text-right', formatFn: this.formatAmountField },
        { label: this.$t('agreements.alarms_short'), field: 'alarm_count', sortable: false, tdClass: 'whitespace-nowrap w-20', thClass: 'whitespace-nowrap w-20' },
        { label: '', field: 'menu', sortable: false, tdClass: 'w-10', thClass: 'w-10' },
        { label: '', field: 'edit', sortable: false, tdClass: 'w-10', thClass: 'w-10' }
      ];
    },
    filteredWarnings() {
      let rows = this.warnings || [];

      if (this.warningFilter !== 'all') {
        rows = rows.filter(r => r.warning_keys.includes(this.warningFilter));
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        rows = rows.filter(r => 
          (r.agreement_number && r.agreement_number.toLowerCase().includes(q)) ||
          (r.customer_name && r.customer_name.toLowerCase().includes(q))
        );
      }

      return rows;
    }
  },
  methods: {
    hide(name) {
      this.$modal.hide(name);
    },
    formatAmountField(value) {
      return this.formatAmount(value);
    },
    getStatusBadgeClass(status) {
      const baseClasses = 'inline-block px-2 py-1 text-xs font-semibold rounded';
      const statusClasses = {
        'active': 'text-green-700 bg-green-100',
        'extended': 'text-blue-700 bg-blue-100',
        'pending': 'text-yellow-700 bg-yellow-100',
        'dismissed': 'text-orange-700 bg-orange-100',
        'returned': 'text-gray-700 bg-gray-200',
        'redrawn': 'text-purple-700 bg-purple-100',
        'active_old': 'text-gray-400 bg-gray-100'
      };
      return `${baseClasses} ${statusClasses[status] || 'text-gray-500 bg-gray-100'}`;
    },
    getWarningBadgeClass(key) {
      const base = 'inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border';
      switch (key) {
        case 'invoice_without_pdf':
          return `${base} bg-red-50 text-red-700 border-red-200`;
        case 'without_alarms':
          return `${base} bg-amber-50 text-amber-700 border-amber-200`;
        case 'extended':
          return `${base} bg-blue-50 text-blue-700 border-blue-200`;
        case 'dismissed_past_cancellation_date':
          return `${base} bg-orange-50 text-orange-700 border-orange-200`;
        case 'dismissed_with_devices':
          return `${base} bg-purple-50 text-purple-700 border-purple-200`;
        default:
          return `${base} bg-gray-50 text-gray-700 border-gray-200`;
      }
    },
    handleEditAgreement(agreement) {
      this.hide('agreements-warnings');
      this.$nextTick(() => {
        this.$emit('edit-agreement', agreement);
      });
    },
    canActOnRow(agreement) {
      if (!this.canEdit) return false;
      if (this.canActFn) return this.canActFn(agreement);
      return true;
    }
  }
};
</script>
